import type { ReactNode } from "react";
import { Check, CheckCircle2, CircleDashed, Info, MinusCircle, RotateCw, XCircle } from "lucide-react";
import { AppLink, PopoverHeading, cx, formatDateTime } from "@amber/ui";
import type {
  ConfidenceView,
  EvidenceRecord,
  PersonReadiness,
  SpeakerView,
  TurnCheck,
  TurnView,
} from "../../contracts/index";
import { capacityText, clock, percent } from "../format";

const RESULT_ICON = {
  passed: <CheckCircle2 size={12} className="text-done" aria-hidden="true" />,
  blocked: <XCircle size={12} className="text-attention" aria-hidden="true" />,
  not_run: <CircleDashed size={12} className="text-muted" aria-hidden="true" />,
  inapplicable: <MinusCircle size={12} className="text-muted" aria-hidden="true" />,
} as const;
const RESULT_TEXT = {
  passed: "passed",
  blocked: "blocked",
  not_run: "not run",
  inapplicable: "not applicable",
} as const;

function Confidence({ label, c }: { label: string; c: ConfidenceView | null }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        {c?.value != null ? (
          <>
            <span className="font-mono">{c.value.toFixed(2)}</span>{" "}
            <span className="text-muted">
              · {c.label}, {c.calibration}
            </span>
          </>
        ) : (
          <span className="text-muted">{c?.reason ?? "unavailable"}</span>
        )}
      </dd>
    </>
  );
}

function PersonBlock({ person }: { person: PersonReadiness }) {
  const failing = person.dimensions.filter((d) => d.result === "blocked" || d.result === "not_run");
  return (
    <div className="mt-2 border-t border-divider pt-2 first:mt-0 first:border-0 first:pt-0">
      <p className="font-strong">
        {person.result === "eligible" ? "Eligible" : "Draft"} for {person.name ?? "no tracked person"}
        {person.reason ? <span className="font-normal text-muted">: {person.reason}</span> : null}
      </p>
      {failing.length > 0 ? (
        <ul className="m-0 list-none p-0 text-md">
          {failing.map((d) => (
            <li key={d.dimension} className="flex items-center gap-1.5">
              {RESULT_ICON[d.result]}
              <span>{d.label}</span>
              <span className="text-muted">
                · {RESULT_TEXT[d.result]}
                {d.reason ? `: ${d.reason}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function ReadinessContent({ record }: { record: EvidenceRecord }) {
  const r = record.readiness;
  if (!r) return <p>Readiness is assessed once speakers are identified.</p>;
  const many = record.communications.length > 1;
  return (
    <div data-testid="readiness-popover">
      <PopoverHeading icon={<Info aria-hidden="true" />}>
        {r.result === "eligible" ? "Eligible" : "Draft"}
        {many ? (
          <span className="font-normal text-muted">
            {" "}
            · {r.eligible_communications} of {record.communications.length} communications Eligible
          </span>
        ) : null}
      </PopoverHeading>
      {r.reason ? <p>{r.reason}</p> : null}
      {record.communications.map((c) => (
        <div key={c.id} className="mt-1.5">
          {many ? <p className="font-semibold">{c.title ?? `Communication ${String(c.ordinal)}`}</p> : null}
          {(c.readiness?.people ?? []).map((p) => (
            <PersonBlock key={p.profile_id ?? "none"} person={p} />
          ))}
          {c.readiness?.people.some((p) => p.profile_id !== null) ? (
            <p className="mt-1.5 text-md text-muted">
              Assessed per tracked person; other speakers' turns are context only.
            </p>
          ) : null}
        </div>
      ))}
      <dl>
        <dt>Policy</dt>
        <dd>
          {r.policy.name}
          {r.policy.provisional ? " · provisional, not validated" : ""}{" "}
          <span className="text-muted">
            · version {r.policy.version}, {formatDateTime(r.policy.applied_at)}
          </span>
        </dd>
        <dt>Evaluated</dt>
        <dd>{formatDateTime(r.assessed_at)} · automatic</dd>
      </dl>
      <p className="mt-2 text-md text-muted">
        Readiness is separate from processing.{" "}
        <AppLink href="/settings/checks" className="text-link underline">
          Checks and policies
        </AppLink>
      </p>
    </div>
  );
}

function attemptsText(record: EvidenceRecord, f: NonNullable<EvidenceRecord["failure"]>): string {
  if (f.attempts.length > 0) return `${String(f.attempts.length)} of ${String(f.attempts[0]?.of ?? f.attempts.length)}`;
  return record.status.attempts
    ? `${String(record.status.attempts.used)} of ${String(record.status.attempts.allowed)}`
    : "none recorded";
}

export function FailureContent({ record }: { record: EvidenceRecord }) {
  const f = record.failure;
  if (!f) return null;
  return (
    <div>
      <PopoverHeading icon={<XCircle aria-hidden="true" className="text-attention" />}>{f.title}</PopoverHeading>
      {f.reason ? <p>{f.reason}</p> : null}
      <dl>
        <dt>Attempts</dt>
        <dd>{attemptsText(record, f)}</dd>
        {f.next ? (
          <>
            <dt>Next step</dt>
            <dd>{f.next}</dd>
          </>
        ) : null}
      </dl>
      {f.attempts.at(-1)?.engine_href ? (
        <p className="mt-2 text-md">
          <a
            href={f.attempts.at(-1)?.engine_href ?? "#"}
            target="_blank"
            rel="noreferrer noopener"
            className="text-link"
          >
            Open engine run
          </a>
        </p>
      ) : null}
    </div>
  );
}

function SpeakerProfile({ speaker }: { speaker: SpeakerView }) {
  if (speaker.profile)
    return <AppLink href={`/profiles?profile=${speaker.profile.id}`}>{speaker.profile.name}</AppLink>;
  if (speaker.profile_match.candidates.length > 0)
    return (
      <>
        Possible profiles: {speaker.profile_match.candidates.map((c) => c.name).join(", ")}
        {speaker.profile_match.reason ? ` · ${speaker.profile_match.reason}` : ""}
      </>
    );
  return <>No profile{speaker.profile_match.reason ? ` · ${speaker.profile_match.reason}` : ""}</>;
}

export function SpeakerContent({
  speaker,
  onSeek,
  confirmed,
}: {
  speaker: SpeakerView;
  onSeek?: (ms: number) => void;
  confirmed?: TurnView["confirmed"];
}) {
  return (
    <div>
      <PopoverHeading>
        {speaker.display}
        <span className="font-normal text-muted"> · {speaker.kind_label}</span>
      </PopoverHeading>
      {speaker.role ? (
        <p className="text-muted">
          {speaker.role.value}
          {speaker.role.inferred ? " (inferred)" : ""}
        </p>
      ) : null}
      {speaker.capacity ? <p className="text-muted">{capacityText(speaker.capacity)}</p> : null}
      <dl>
        <Confidence label="Name" c={speaker.naming.confidence} />
        <Confidence label="Profile match" c={speaker.profile_match.confidence} />
        <Confidence label="Attribution" c={speaker.lowest_attribution} />
        {confirmed ? (
          <>
            <dt>Confirmed</dt>
            <dd>
              Confirmed by a person · {confirmed.by}, {formatDateTime(confirmed.at)}{" "}
              <span className="text-muted">· the machine score stays for reference</span>
            </dd>
          </>
        ) : null}
        <dt>Method</dt>
        <dd className="text-muted">{speaker.naming.confidence?.method ?? "contribution-attribution@1"}</dd>
      </dl>
      <p className="mt-1.5 text-md text-muted">
        Three separate scores, never combined. Attribution shows the lowest turn value.
      </p>
      {speaker.naming.evidence.length > 0 ? (
        <div className="mt-1.5 text-md">
          <span className="text-muted">Evidence: </span>
          {speaker.naming.evidence.map((e, i) => (
            <span key={`${e.ref}-${String(i)}`}>
              {i > 0 ? "; " : ""}“{e.text}”
              {e.at_ms !== null ? (
                onSeek ? (
                  <button
                    type="button"
                    className="ml-1 font-mono text-link"
                    onClick={() => {
                      onSeek(e.at_ms ?? 0);
                    }}
                  >
                    at {clock(e.at_ms)}
                  </button>
                ) : (
                  <span className="ml-1 font-mono">at {clock(e.at_ms)}</span>
                )
              ) : (
                <span className="text-muted"> in the metadata</span>
              )}
            </span>
          ))}
        </div>
      ) : null}
      {speaker.naming.alternatives.length > 0 ? (
        <p className="mt-1.5 text-md">
          Alternatives considered:{" "}
          {speaker.naming.alternatives.map((a) => `${a.name} (${a.confidence.toFixed(2)})`).join(", ")}
        </p>
      ) : null}
      {speaker.naming.reason ? <p className="mt-1.5 text-md">{speaker.naming.reason}</p> : null}
      <p className="mt-1.5 text-md">
        <SpeakerProfile speaker={speaker} />
      </p>
      <p className="mt-1 text-md text-muted">Unresolved speakers and speakers without a profile are normal results.</p>
    </div>
  );
}

export function WordContent({
  word,
  onPlay,
  readOnlyNote,
  correction,
}: {
  word: TurnView["words"][number];
  onPlay: () => void;
  readOnlyNote: ReactNode | null;
  correction?: ReactNode;
}) {
  return (
    <div>
      <PopoverHeading>“{word.text}”</PopoverHeading>
      {word.corrected ? (
        <dl>
          <dt>Was</dt>
          <dd>
            {word.corrected.was === null ? (
              <span className="text-muted">not in the generated transcript</span>
            ) : (
              `“${word.corrected.was}”`
            )}
          </dd>
          <dt>Now</dt>
          <dd>“{word.text}”</dd>
          <dt>By</dt>
          <dd>
            {word.corrected.by} · {formatDateTime(word.corrected.at)}
          </dd>
          <dt>Reason</dt>
          <dd>{word.corrected.reason}</dd>
          {word.corrected.from ? (
            <>
              <dt>Copy</dt>
              <dd>Carried over from {word.corrected.from}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      <dl>
        <dt>Confidence</dt>
        <dd>
          <span className="font-mono">{percent(word.confidence)}</span>{" "}
          <span className="text-muted">· provider score, uncalibrated</span>
        </dd>
        <dt>Time</dt>
        <dd className="font-mono">{clock(word.start_ms)}</dd>
        <dt>Alternatives</dt>
        <dd className="text-muted">None supplied by the provider</dd>
      </dl>
      <div className="mt-2 flex items-center gap-2">
        <button type="button" className="text-md text-link" onClick={onPlay}>
          Play from {clock(word.start_ms)}
        </button>
      </div>
      {correction ?? (readOnlyNote ? <p className="mt-2 text-md text-muted">{readOnlyNote}</p> : null)}
    </div>
  );
}

export function PassageContent({ turn }: { turn: TurnView }) {
  return (
    <div>
      <PopoverHeading>{turn.passage.mark === "flagged" ? "Flagged passage" : "Uncertain passage"}</PopoverHeading>
      {turn.passage.reasons.map((r) => (
        <p key={r}>{r}</p>
      ))}
      <dl>
        <dt>Passage</dt>
        <dd>
          {turn.passage.confidence === null ? (
            "no confidence available"
          ) : (
            <span className="font-mono">{turn.passage.confidence.toFixed(2)}</span>
          )}
        </dd>
      </dl>
      <p className="mt-1.5 text-md text-muted">
        Uncertain words inside keep their own underline. Next step: listen and correct, or request a passage check.
      </p>
    </div>
  );
}

const DIMENSION_ICON: Record<TurnCheck["dimensions"][number]["outcome"], ReactNode> = {
  passed: <Check size={13} aria-hidden="true" />,
  failed: <XCircle size={13} aria-hidden="true" />,
  insufficient: <CircleDashed size={13} aria-hidden="true" />,
  not_run: <CircleDashed size={13} aria-hidden="true" />,
};
const DIMENSION_SR: Record<TurnCheck["dimensions"][number]["outcome"], ReactNode> = {
  passed: <span className="sr-only"> passed</span>,
  failed: <span className="sr-only"> failed</span>,
  insufficient: null,
  not_run: null,
};
const OTHER_ICON: Record<TurnCheck["other"][number]["state"], ReactNode> = {
  not_applicable: <MinusCircle size={13} aria-hidden="true" />,
  recorded: <Check size={13} aria-hidden="true" />,
  not_run: <CircleDashed size={13} aria-hidden="true" />,
};

function checkHeading(mark: TurnCheck["mark"]): { icon: ReactNode; title: string } {
  if (mark === "failed")
    return { icon: <XCircle aria-hidden="true" className="text-attention" />, title: "A passage check failed" };
  if (mark === "verified")
    return { icon: <CheckCircle2 aria-hidden="true" className="text-done" />, title: "Passage verified" };
  return { icon: <CircleDashed aria-hidden="true" className="text-muted" />, title: "Passage not verified" };
}

const OUTCOME_TAG: Record<TurnCheck["dimensions"][number]["outcome"], string | null> = {
  passed: null,
  failed: null,
  insufficient: "Insufficient",
  not_run: "Not run",
};
const OTHER_TAG: Record<TurnCheck["other"][number]["state"], string> = {
  not_run: "Not run",
  not_applicable: "Not applicable",
  recorded: "Recorded",
};

function StateTag({ children, dashed }: { children: ReactNode; dashed?: boolean }) {
  return (
    <span
      className={cx(
        "mr-1 rounded-full border border-border px-1.5 text-2xs whitespace-nowrap text-muted",
        dashed && "border-dashed",
      )}
    >
      {children}
    </span>
  );
}

function CheckRow({ icon, children, failed }: { icon: ReactNode; children: ReactNode; failed?: boolean }) {
  return (
    <li
      className={cx(
        "grid grid-cols-[16px_1fr] gap-1.5 py-[3px] text-md",
        failed ? "[&_svg]:text-attention" : "[&_svg]:text-muted",
      )}
    >
      <span className="pt-0.5">{icon}</span>
      <span>{children}</span>
    </li>
  );
}

export function CheckContent({ check }: { check: TurnCheck }) {
  if (check.mark === "rechecking") {
    const running = check.dimensions
      .filter((d) => d.outcome === "not_run" && d.detail !== "Not requested")
      .map((d) => d.label);
    return (
      <div data-testid="check-popover">
        <PopoverHeading icon={<RotateCw aria-hidden="true" className="text-muted" />}>
          Rechecking this passage
          <span className="ml-auto font-mono text-md font-normal text-muted">{check.range_label}</span>
        </PopoverHeading>
        <p>The checks for this passage run again; results appear here when they finish.</p>
        <dl>
          <dt>Queued</dt>
          <dd>
            {formatDateTime(check.requested_at)} · {check.requested_by}
          </dd>
          <dt>Runs</dt>
          <dd>{running.length > 0 ? running.join(", ") : "Wording, Position and Context"}</dd>
        </dl>
        <div className="mt-2 text-md text-muted">
          Until the checks finish, the passage counts as unverified. Draft inspection is not affected.
        </div>
      </div>
    );
  }
  const heading = checkHeading(check.mark);
  return (
    <div data-testid="check-popover">
      <PopoverHeading icon={heading.icon}>
        {heading.title}
        <span className="ml-auto font-mono text-md font-normal text-muted">{check.range_label}</span>
      </PopoverHeading>
      <ul aria-label="Check results" className="m-0 list-none p-0">
        {check.dimensions.map((d) => (
          <CheckRow key={d.dimension} failed={d.outcome === "failed"} icon={DIMENSION_ICON[d.outcome]}>
            {OUTCOME_TAG[d.outcome] ? <StateTag dashed>{OUTCOME_TAG[d.outcome]}</StateTag> : null}
            <b className="font-strong">{d.label}</b>
            {DIMENSION_SR[d.outcome]}
            <span className="text-muted"> · {d.detail}</span>
          </CheckRow>
        ))}
        {check.other.map((o) => (
          <CheckRow key={o.name} icon={OTHER_ICON[o.state]}>
            <StateTag dashed={o.state === "not_run"}>{OTHER_TAG[o.state]}</StateTag>
            <span className="font-medium text-muted">{o.name}</span>
            <span className="text-muted"> · {o.detail}</span>
          </CheckRow>
        ))}
      </ul>
      {check.failure_detail ? <p className="mt-1.5">{check.failure_detail}</p> : null}
      <dl>
        <dt>Checked</dt>
        <dd>{check.completed_at ? `${formatDateTime(check.completed_at)} · automatic` : "Not run"}</dd>
        <dt>Against</dt>
        <dd>{check.against}</dd>
        <dt>Requested</dt>
        <dd>
          {check.requested_by} · {formatDateTime(check.requested_at)}
        </dd>
        {check.next ? (
          <>
            <dt>Next</dt>
            <dd>{check.next}</dd>
          </>
        ) : null}
      </dl>
      {check.task_href ? (
        <div className="mt-2 text-md">
          <AppLink href={check.task_href} className="text-link">
            Open the review task
          </AppLink>
        </div>
      ) : null}
      <div className="mt-2 text-md text-muted">
        Checks confirm the wording, position and context were captured faithfully. They do not rate whether the
        statement is true.
      </div>
    </div>
  );
}
